import { ConfirmInputOptions, openConfirmOverlay } from "overlay"
import { LocationUtils } from "../../../../modules/locationUtils"

export namespace DaylightWarning {
	export let needsWarn = true

	/**
	 * Warn the user of potential daylight issues.
	 * If they press "cancel", they will still be warned until pressing my location is dark.
	 */
	export async function warn(): Promise<boolean> {
		const isDark = await confirmIsDark()
		if (!isDark) return false

		needsWarn = false
		return true
	}

	/**
	 * Check if it's daylight and get user confirmation
	 * Resolves with a true/false value if the scene is dark
	 */
	async function confirmIsDark(spoofedLocation?: LocationUtils.Location): Promise<boolean> {
		const location = spoofedLocation ?? (await LocationUtils.getLocation())

		// If we have NO location (i.e., null), fall back to standard day/night times:
		if (!location) {
			const now = new Date()
			const hour = now.getHours() // 0–23

			// Standard day: 6 AM <= hour < 20 (8 PM)
			const isFallbackDay = hour >= 6 && hour < 20

			// If it's not day, it must be dark, so immediately return true (dark = proceed)
			if (!isFallbackDay) return true

			// If it is day, show the same warning popup as before
			const fallbackMessage = `
				Warning: Based on the time of the day, we detected that it might be daylight at your location.
				Calibration will not work if it is too bright.
			`

			return new Promise<boolean>((resolve) => {
				const options: ConfirmInputOptions = {
					prompt: fallbackMessage,
					buttons: {
						confirm: {
							text: "My location is dark",
							onClick: () => resolve(true),
						},
						deny: {
							text: "I will try again when it is dark",
							onClick: () => resolve(false),
						},
					},
				}
				openConfirmOverlay(options, {
					heading: "Possible Daylight Detected",
					classHeading: "no-underline",
				})
			})
		}

		// ===== If we DO have a location, use the real isDaylight() logic =====
		const isDay = await LocationUtils.isDaylight(location, new Date())
		if (!isDay) return true

		// Otherwise, show the confirmation overlay
		return new Promise<boolean>((res) => {
			const option: ConfirmInputOptions = {
				prompt: [
					"Warning: We detected that it might be daylight at your location.",
					"Calibration will not work if it is too bright in your location.",
				],
				buttons: {
					confirm: {
						text: "My location is dark",
						onClick: () => res(true),
					},
					deny: {
						text: "I will try again when it is dark",
						onClick: () => res(false),
					},
				},
			}
			openConfirmOverlay(option, { heading: "Daylight Detected", classHeading: "no-underline" })
		})
	}
}
